import { useState } from "react";
import { Badge, EditRowActions, Input, Switch, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { EquivalenciaUnidad } from "@/lib/types";
import { useEditarEquivalencia, useEliminarEquivalencia } from "../hooks/use-equivalencia-mutations";

/**
 * Fila del catálogo de equivalencias · alterna vista/edición inline. La clave
 * SAT es inmutable (identifica el par); se editan unidad SAP, descripción y el
 * flag activo. Eliminar pide confirmación.
 */
export function EquivalenciaRow({ equivalencia }: { equivalencia: EquivalenciaUnidad }) {
  const [editando, setEditando] = useState(false);
  const [unidadSap, setUnidadSap] = useState(equivalencia.unidadSap);
  const [descripcion, setDescripcion] = useState(equivalencia.descripcion ?? "");
  const [activo, setActivo] = useState(equivalencia.activo);
  const confirm = useConfirmDialog();

  const guardar = useEditarEquivalencia({ onEditado: () => setEditando(false) });
  const eliminar = useEliminarEquivalencia();

  const cancelar = () => {
    setUnidadSap(equivalencia.unidadSap);
    setDescripcion(equivalencia.descripcion ?? "");
    setActivo(equivalencia.activo);
    setEditando(false);
  };

  const confirmarEliminar = async () => {
    const ok = await confirm({
      title: "Dar de baja equivalencia",
      message: (
        <>
          ¿Dar de baja la equivalencia{" "}
          <b>
            {equivalencia.claveUnidadSat} ↔ {equivalencia.unidadSap}
          </b>
          ? Las conciliaciones de OC dejarán de usarla. Se conserva en el catálogo como inactiva y puedes reactivarla editándola.
        </>
      ),
      confirmLabel: "Dar de baja",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(equivalencia.id);
  };

  if (!editando) {
    return (
      <Tr>
        <Td>
          <Text ff="mono" size="sm" fw={500}>
            {equivalencia.claveUnidadSat}
          </Text>
        </Td>
        <Td>
          <Text ff="mono" size="sm">
            {equivalencia.unidadSap}
          </Text>
        </Td>
        <Td>
          <Text size="sm" c="dimmed">
            {equivalencia.descripcion ?? "—"}
          </Text>
        </Td>
        <Td>
          <Badge size="sm" color={equivalencia.activo ? "exito" : "slate"}>
            {equivalencia.activo ? "Activa" : "Inactiva"}
          </Badge>
        </Td>
        <Td align="right">
          <EditRowActions mode="view" onEdit={() => setEditando(true)} onDelete={confirmarEliminar} deleting={eliminar.isPending} />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr className="bg-muted/50">
      <Td>
        <Text ff="mono" size="sm" fw={500}>
          {equivalencia.claveUnidadSat}
        </Text>
      </Td>
      <Td>
        <Input size="xs" value={unidadSap} onChange={(e) => setUnidadSap(e.currentTarget.value.toUpperCase())} autoFocus />
      </Td>
      <Td>
        <Input size="xs" value={descripcion} onChange={(e) => setDescripcion(e.currentTarget.value)} />
      </Td>
      <Td>
        <Switch checked={activo} onCheckedChange={setActivo} label={activo ? "Activa" : "Inactiva"} />
      </Td>
      <Td align="right">
        <EditRowActions
          mode="edit"
          saving={guardar.isPending}
          onCancel={cancelar}
          onSave={() => guardar.mutate({ id: equivalencia.id, input: { unidadSap: unidadSap.trim(), descripcion: descripcion.trim() || null, activo } })}
        />
      </Td>
    </Tr>
  );
}
